import { activeLeaveRules } from '@/constant/hr/leave-rule-catalog';
import { blocksClient } from '@/lib/blocks/client';
import { graphqlClient } from '@/lib/graphql-client';
import {
  dateOnlyOf,
  dateOnlyRangeClause,
  orgCalendarDate,
} from '@/lib/utils/custom-date/custom-date';
import { getUserRolesInOrganization } from '@/modules/hr-directory/services/work-status-eligibility.service';
import {
  buildGetAttendancesForSheetQuery,
  buildGetHolidaysForSheetQuery,
  buildGetHrTermDetailForLeaveSheetQuery,
  buildGetHrTermsForLeaveSheetQuery,
  buildGetLeavesForSheetQuery,
  buildGetPendingCorrectionsForSheetQuery,
  buildGetToilsForSheetQuery,
} from '../graphql/leave-sheet-queries';
import {
  LeaveSheetAllotment,
  LeaveSheetAttendance,
  LeaveSheetCorrection,
  LeaveSheetHoliday,
  LeaveSheetLeave,
  LeaveSheetPolicy,
  LeaveSheetResult,
  LeaveSheetTerm,
  LeaveSheetToil,
  LeaveSheetWorkDay,
} from '../types/leave-sheet.types';
import { computeLeaveSheet } from '../utils/leave-sheet-ledger';

interface HrTermRaw {
  ItemId: string;
  UserId?: string;
  HRPeriod?: string;
  HRPeriodName?: string;
  HRPeriodStartDate: string;
  HRPeriodEndDate: string;
  IsCurrentHrYear?: boolean;
  EmploymentType?: string;
  IsFlexible?: boolean;
  LeaveCarryForwardDays?: number;
  ToilCarryForwardDays?: number;
  SpecialToilCarryForwardDays?: number;
  WorkDays?: LeaveSheetWorkDay[];
  LeaveAllotments?: LeaveSheetAllotment[];
}

// Generous upper bounds for one employee's term (a year of days) and one organization's holidays.
const SHEET_PAGE_SIZE = 500;
const TERMS_PAGE_SIZE = 50;
const LEAVE_RESOURCE_GROUP = 'leave';

/** The sheet's part of the employee's leave rules (`@/constant/hr/leave-rule-catalog`). */
export const leaveSheetPolicyOf = (held: (permission: string) => boolean): LeaveSheetPolicy => {
  const rules = activeLeaveRules(held);
  return {
    toil: rules.toilBalance
      ? rules.toilBalance.params.splitByCategory
        ? 'split'
        : 'combined'
      : null,
    deductMissedAttendance: Boolean(rules.missedAttendance),
    exemptFlexible: rules.missedAttendance?.params.exemptFlexible ?? true,
  };
};

/**
 * The leave permissions another employee holds through their roles in the organization: the roles
 * each `leave` permission is assigned to (IAM lists them on the permission) against the employee's
 * roles, the same two-sided read as `work-status-eligibility.service.ts`. The signed-in user's own
 * come from their token instead.
 */
export const getEmployeeLeavePermissions = async (
  userId: string,
  organizationId?: string
): Promise<string[]> => {
  const [response, roles] = await Promise.all([
    blocksClient.iam.permissions.list({
      filter: { resourceGroup: LEAVE_RESOURCE_GROUP },
      page: 0,
      pageSize: 100,
    }) as Promise<
      { data?: { resource?: string; roles?: unknown; isArchived?: boolean }[] } | undefined
    >,
    getUserRolesInOrganization(userId, organizationId),
  ]);
  return (response?.data ?? [])
    .filter(
      (permission) =>
        permission.resource &&
        !permission.isArchived &&
        Array.isArray(permission.roles) &&
        permission.roles.some((role) => typeof role === 'string' && roles.includes(role))
    )
    .map((permission) => permission.resource as string);
};

const toTerm = (raw: HrTermRaw): LeaveSheetTerm => ({
  ItemId: raw.ItemId,
  HRPeriod: raw.HRPeriod,
  HRPeriodName: raw.HRPeriodName,
  periodStart: dateOnlyOf(raw.HRPeriodStartDate),
  periodEnd: dateOnlyOf(raw.HRPeriodEndDate),
  isCurrent: Boolean(raw.IsCurrentHrYear),
  toilCarryForwardDays: raw.ToilCarryForwardDays ?? 0,
  specialToilCarryForwardDays: raw.SpecialToilCarryForwardDays ?? 0,
});

/** The employee's HR terms, newest first. */
export const getLeaveSheetTerms = async (userId: string): Promise<LeaveSheetTerm[]> => {
  if (!userId) return [];
  const result = await graphqlClient.query<{ HrProfiles: { items: HrTermRaw[] } }>({
    query: buildGetHrTermsForLeaveSheetQuery({ UserId: userId }, TERMS_PAGE_SIZE),
  });
  return result.HrProfiles.items.map(toTerm);
};

/** The day before a plain "YYYY-MM-DD" day (calendar arithmetic, no zone). */
const previousDay = (day: string): string => {
  const [year, month, date] = day.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, date - 1)).toISOString().slice(0, 10);
};

/**
 * One employee's leave sheet for an HR term (`termId`, or the current one): the term, its leave and
 * TOIL requests, holidays and, under the missed-attendance rule, attendance, reduced by
 * `computeLeaveSheet`. Like legacy, a running term is shown up to yesterday in the organization's
 * zone (today isn't over). `null` when the employee has no such term.
 */
export const getLeaveSheet = async ({
  userId,
  termId,
  policy,
}: {
  userId: string;
  termId?: string;
  policy: LeaveSheetPolicy;
}): Promise<LeaveSheetResult | null> => {
  if (!userId) return null;

  const termResult = await graphqlClient.query<{ HrProfiles: { items: HrTermRaw[] } }>({
    query: buildGetHrTermDetailForLeaveSheetQuery(
      termId ? { UserId: userId, ItemId: termId } : { UserId: userId, IsCurrentHrYear: true }
    ),
  });
  const raw = termResult.HrProfiles.items[0];
  if (!raw) return null;

  const term = toTerm(raw);
  const yesterday = previousDay(orgCalendarDate(new Date().toISOString()));
  const lastDay = yesterday < term.periodEnd ? yesterday : term.periodEnd;
  const periodRange = dateOnlyRangeClause(term.periodStart, term.periodEnd);
  const ledgerRange = dateOnlyRangeClause(term.periodStart, lastDay);

  const empty = { items: [] };
  const [leaveResult, toilResult, holidayResult, attendanceResult, correctionResult] =
    await Promise.all([
      periodRange
        ? graphqlClient.query<{ LeaveRequests: { items: LeaveSheetLeave[] } }>({
            query: buildGetLeavesForSheetQuery(
              {
                UserId: userId,
                Status: { $in: ['Approved', 'Pending'] },
                StartDate: { $lt: periodRange.$lt },
                EndDate: { $gte: periodRange.$gte },
              },
              SHEET_PAGE_SIZE
            ),
          })
        : { LeaveRequests: empty },
      policy.toil && ledgerRange
        ? graphqlClient.query<{ ToilRequests: { items: LeaveSheetToil[] } }>({
            query: buildGetToilsForSheetQuery(
              { UserId: userId, Status: 'Approved', ToilDate: ledgerRange },
              SHEET_PAGE_SIZE
            ),
          })
        : { ToilRequests: empty },
      // The whole term's: the ledger walks up to lastDay, the other categories count every request of the term.
      periodRange
        ? graphqlClient.query<{ Holidays: { items: LeaveSheetHoliday[] } }>({
            query: buildGetHolidaysForSheetQuery({ StartDate: periodRange }, SHEET_PAGE_SIZE),
          })
        : { Holidays: empty },
      policy.deductMissedAttendance && ledgerRange
        ? graphqlClient.query<{ Attendances: { items: LeaveSheetAttendance[] } }>({
            query: buildGetAttendancesForSheetQuery(
              { UserId: userId, AttendanceDate: ledgerRange },
              SHEET_PAGE_SIZE
            ),
          })
        : { Attendances: empty },
      policy.deductMissedAttendance && ledgerRange
        ? graphqlClient.query<{ AttendanceCorrections: { items: LeaveSheetCorrection[] } }>({
            query: buildGetPendingCorrectionsForSheetQuery(
              { UserId: userId, Status: 'Pending', CorrectionDate: ledgerRange },
              SHEET_PAGE_SIZE
            ),
          })
        : { AttendanceCorrections: empty },
    ]);

  const { summary, rows, categories } = computeLeaveSheet({
    periodStart: term.periodStart,
    periodEnd: term.periodEnd,
    lastDay,
    workDays: raw.WorkDays ?? [],
    allotments: raw.LeaveAllotments ?? [],
    employmentType: raw.EmploymentType,
    leaveCarryForwardDays: raw.LeaveCarryForwardDays ?? 0,
    toilCarryForwardDays: raw.ToilCarryForwardDays ?? 0,
    specialToilCarryForwardDays: raw.SpecialToilCarryForwardDays ?? 0,
    isFlexible: Boolean(raw.IsFlexible),
    holidays: holidayResult.Holidays.items,
    leaves: leaveResult.LeaveRequests.items,
    toils: toilResult.ToilRequests.items,
    attendances: attendanceResult.Attendances.items,
    pendingCorrections: correctionResult.AttendanceCorrections.items,
    policy,
  });

  return { term, policy, lastDay, summary, rows, categories };
};
